import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
  Chip,
  InputAdornment,
  Switch,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ListItemIcon,
  Tooltip,
  Menu,
  MenuItem,
  Alert,
  Snackbar,
  Container,
  CircularProgress,
  FormControlLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadIcon from '@mui/icons-material/Upload';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LabelIcon from '@mui/icons-material/Label';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ArticleIcon from '@mui/icons-material/Article';
import BookIcon from '@mui/icons-material/Book';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { useLang } from '../../contexts/langContext';
import { useMaterials } from '../../hooks/useMaterials';
import TextIcon from '@mui/icons-material/TextFields';
import FileIcon from '@mui/icons-material/FilePresent';
import LinkIcon from '@mui/icons-material/Link';
import MaterialPicker from '../materials/MaterialPicker';
import ManageTagsDialog from '../common/ManageTagsDialog';
import FileUploadDialog from '../common/FileUploadDialog';
import TextCreationDialog from '../common/TextCreationDialog';
import { copyMaterialToLibrary } from '../../services/materials';
import { useTags } from '../../hooks/useTags';
import type { Tag, MaterialWithFileMetadata } from '../../types';
import { useBranding } from '../../contexts/BrandingContext';
import { useProject } from '../../hooks/useProject';
import type { Project, ProjectUpdate } from '../../types';

interface MaterialsTabProps {
  projectId?: string;
  selectedTagIds?: string[]; // empty array = all
  onCountsChange?: (counts: Record<string | 'all', number>) => void;
  project?: Project | null;
  onUpdateProject?: (projectData: ProjectUpdate) => Promise<void>;
}

function MaterialList({ projectId, selectedTagIds = [], onCountsChange, project: projectProp, onUpdateProject }: MaterialsTabProps) {
  const { features } = useBranding();
  const verbatimMode = features?.enableVerbatimMode ?? true;
  const navigate = useNavigate();
  const { lang } = useLang();
  const { tags } = useTags();

  // Use props if provided (from parent), otherwise use hook
  const { project: projectFromHook, handleUpdateProject: handleUpdateProjectFromHook } = useProject(projectProp ? undefined : projectId);
  const project = projectProp || projectFromHook;
  console.log('project:', project);
  const handleUpdateProject = onUpdateProject || handleUpdateProjectFromHook;
  const tagMap = useMemo(() => {
    const map: Record<string, Tag> = {};
    tags.forEach((t) => {
      map[t._id] = t;
    });
    return map;
  }, [tags]);
  const [materialToDelete, setMaterialToDelete] = useState<string | null>(null);
  const [materialToDeleteName, setMaterialToDeleteName] = useState<string>('');
  const [materialToDeleteType, setMaterialToDeleteType] = useState<string>('');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    material: MaterialWithFileMetadata;
  } | null>(null);
  const [manageTagsDialogOpen, setManageTagsDialogOpen] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState<MaterialWithFileMetadata | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string>('');
  const [feedbackType, setFeedbackType] = useState<'success' | 'warning' | 'error'>('success');
  const [verbatimDisableDialogOpen, setVerbatimDisableDialogOpen] = useState(false);
  const [pendingVerbatimMaterialId, setPendingVerbatimMaterialId] = useState<string | null>(null);
  const [isProcessingVerbatimDisable, setIsProcessingVerbatimDisable] = useState(false);
  const {
    materials,
    searchQuery,
    isSearching,
    isLoading,
    isCreatingText,
    isUploadingFile,
    error,
    fetchMaterials,
    createMaterial,
    createTextMaterial,
    deleteMaterial,
    toggleMaterialAutoReferenced,
    handleSearch,
    associateMaterialsWithProject,
    setSearchQuery,
  } = useMaterials({ projectId, initialFetch: true });

  // Calculate and report tag counts whenever materials change
  useEffect(() => {
    if (onCountsChange) {
      const counts: Record<string | 'all', number> = { all: materials.length };
      materials.forEach((material) => {
        (material.tagIds || []).forEach((tagId) => {
          counts[tagId] = (counts[tagId] || 0) + 1;
        });
      });
      onCountsChange(counts);
    }
  }, [materials, onCountsChange]);

  // Filter materials based on selected tags
  const displayedMaterials = selectedTagIds.length === 0 ? materials : materials.filter((m) => selectedTagIds.some((tid) => m.tagIds?.includes(tid)));

  const handleToggleVerbatimMode = async (materialId: string, enabled: boolean, options?: { clearSlides?: boolean }) => {
    if (!projectId || !project) return;

    try {
      const updatePayload: ProjectUpdate = {
        verbatimMaterialId: enabled ? materialId : null,
      };

      if (!enabled && options?.clearSlides) {
        updatePayload.slides = [];
      }

      // Update the project with the new verbatimMaterialId (use null to clear it)
      await handleUpdateProject(updatePayload);

      const successMessage =
        lang === 'en'
          ? enabled
            ? 'Verbatim mode enabled for this material'
            : options?.clearSlides
            ? 'Verbatim mode disabled and slides cleared'
            : 'Verbatim mode disabled'
          : enabled
          ? '已为此材料启用逐字转换模式'
          : options?.clearSlides
          ? '已禁用逐字转换模式并清空幻灯片'
          : '已禁用逐字转换模式';

      setFeedbackMessage(successMessage);
      setFeedbackType('success');
      setTimeout(() => setFeedbackMessage(''), 3000);
    } catch (error) {
      console.error('Error toggling verbatim mode:', error);
      setFeedbackMessage(lang === 'en' ? 'Error updating verbatim mode' : '更新逐字转换模式时出错');
      setFeedbackType('error');
      setTimeout(() => setFeedbackMessage(''), 3000);
    }
  };

  const handleVerbatimSwitchChange = (materialId: string, enabled: boolean) => {
    if (!enabled && project?.slides && project.slides.length > 0 && project.verbatimMaterialId === materialId) {
      setPendingVerbatimMaterialId(materialId);
      setVerbatimDisableDialogOpen(true);
      return;
    }

    handleToggleVerbatimMode(materialId, enabled);
  };

  const handleConfirmDisableVerbatim = async (shouldClearSlides: boolean) => {
    if (!pendingVerbatimMaterialId) return;
    setIsProcessingVerbatimDisable(true);

    try {
      await handleToggleVerbatimMode(pendingVerbatimMaterialId, false, { clearSlides: shouldClearSlides });
      setVerbatimDisableDialogOpen(false);
      setPendingVerbatimMaterialId(null);
    } finally {
      setIsProcessingVerbatimDisable(false);
    }
  };

  const handleCancelDisableVerbatim = () => {
    if (isProcessingVerbatimDisable) return;
    setVerbatimDisableDialogOpen(false);
    setPendingVerbatimMaterialId(null);
  };

  const handleFileUpload = async (file: File) => {
    const formData = new FormData();
    const fileBlob = new Blob([file], { type: file.type });
    formData.append('file', fileBlob, file.name);
    formData.append('type', file.type.includes('text') ? 'text' : 'docx');

    try {
      await createMaterial(formData);
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };

  const handleCreateText = async (filename: string, content: string) => {
    try {
      await createTextMaterial({
        filename,
        content,
      });
    } catch (error) {
      console.error('Error creating text:', error);
    }
  };

  const handleSearchSubmit = () => {
    if (!searchQuery.trim()) return;
    navigate(`${projectId ? `/projects/${projectId}` : ''}/materials/search?q=${encodeURIComponent(searchQuery)}`);
  };

  const handleMaterialClick = (materialId: string) => {
    navigate(projectId ? `/projects/${projectId}/materials/${materialId}` : `/materials/${materialId}`);
  };

  const handleConfirmDelete = async () => {
    if (materialToDelete) {
      const isVerbatimMaterial = project?.verbatimMaterialId === materialToDelete;

      await deleteMaterial(materialToDelete);

      // If we just deleted the verbatim material, refresh the project so verbatimMaterialId is cleared in state
      if (isVerbatimMaterial && projectId && project) {
        await handleUpdateProject({ verbatimMaterialId: null });
      }

      setIsDeleteDialogOpen(false);
      setMaterialToDelete(null);
    }
  };

  const getIcon = (type: string, size: number = 24) => {
    if (type.includes('text')) return <TextIcon sx={{ color: 'text.secondary', fontSize: size }} />;
    return <FileIcon sx={{ color: 'text.secondary', fontSize: size }} />;
  };

  const handleSelectMaterials = async (materials: MaterialWithFileMetadata[]) => {
    if (!projectId) return;

    try {
      console.log('materials', materials);
      // Filter out materials that are already in this project
      const nonCurrentProjectMaterials = materials.filter((m) => !m.projectId || m.projectId.toString() !== projectId);
      // Get the IDs of non-project materials to associate
      const materialIdsToAssociate = nonCurrentProjectMaterials.map((m) => m._id);

      // Associate materials with the project
      await associateMaterialsWithProject(materialIdsToAssociate);

      // Refresh the materials list after association
      await fetchMaterials();

      // Close the dialog
      // setIsPickerDialogOpen(false);
    } catch (error) {
      console.error('Error associating materials with project:', error);
    }
  };

  const handleContextMenu = (event: React.MouseEvent, material: MaterialWithFileMetadata) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({
      mouseX: event.clientX,
      mouseY: event.clientY,
      material,
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleManageTags = () => {
    if (contextMenu) {
      setSelectedMaterial(contextMenu.material);
      setManageTagsDialogOpen(true);
    }
    handleCloseContextMenu();
  };

  const handleDeleteFromContext = () => {
    if (contextMenu) {
      setMaterialToDelete(contextMenu.material._id);
      setMaterialToDeleteName(contextMenu.material.fileMetadata?.filename || 'Untitled');
      setMaterialToDeleteType(contextMenu.material.fileMetadata?.type || '');
      setIsDeleteDialogOpen(true);
    }
    handleCloseContextMenu();
  };

  const handleToggleAutoReferenced = async () => {
    if (contextMenu) {
      try {
        await toggleMaterialAutoReferenced(contextMenu.material._id);
      } catch (error) {
        console.error('Error toggling for reference only:', error);
      }
    }
    handleCloseContextMenu();
  };

  const handleTagsSaved = (newTagIds: string[]) => {
    // Refresh materials after tag update
    fetchMaterials();
  };

  const handleCopyToLibrary = async () => {
    if (contextMenu && projectId) {
      try {
        const result = await copyMaterialToLibrary(projectId, contextMenu.material._id);
        if (result.existing) {
          setFeedbackMessage(result.message);
          setFeedbackType('warning');
        } else {
          setFeedbackMessage(result.message);
          setFeedbackType('success');
        }
        // Auto-hide feedback after 3 seconds
        setTimeout(() => setFeedbackMessage(''), 3000);
      } catch (error) {
        console.error('Error copying material to library:', error);
        const errorMessage = (error as any).response?.data?.message || (lang === 'en' ? 'Error copying material to library' : '复制材料到库时出错');
        setFeedbackMessage(errorMessage);
        setFeedbackType('error');
        setTimeout(() => setFeedbackMessage(''), 3000);
      }
    }
    handleCloseContextMenu();
  };

  return (
    <Box sx={{ maxWidth: 'md', mx: 'auto' }}>
      {displayedMaterials.length > 0 ? (
        <>
          <Stack direction="row" spacing={2} mb={3} justifyContent="space-between">
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              placeholder={lang === 'en' ? 'Search materials...' : '搜索材料...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
              sx={{ maxWidth: '300px' }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleSearchSubmit} disabled={isSearching}>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" spacing={1} flexGrow={1} justifyContent="flex-end">
              {projectId && (
                <MaterialPicker
                  onSelect={handleSelectMaterials}
                  multiple={true}
                  initialSelected={materials.map((m) => m.fileServiceFileId)}
                  title={lang === 'en' ? 'Select Materials' : '选择材料'}
                  lang={lang}
                >
                  {(openDialog) => (
                    <Tooltip
                      title={
                        lang === 'en'
                          ? 'Link materials from existing library to current project for AI reference'
                          : '从现有材料库中链接材料到当前项目以供AI参考'
                      }
                    >
                      <Button sx={{ flexShrink: 0 }} startIcon={<LinkIcon />} onClick={openDialog}>
                        {lang === 'en' ? 'Link Materials to Project' : '链接材料到项目'}
                      </Button>
                    </Tooltip>
                  )}
                </MaterialPicker>
              )}
              <TextCreationDialog onCreate={handleCreateText} lang={lang} isCreating={isCreatingText}>
                {(open) => (
                  <Button
                    sx={{ flexShrink: 0 }}
                    onClick={open}
                    disabled={isCreatingText}
                    startIcon={isCreatingText ? <CircularProgress size={20} /> : <AddIcon />}
                  >
                    {lang === 'en' ? 'Create Text' : lang === 'zh-HK' ? '创建文本' : '创建文本'}
                  </Button>
                )}
              </TextCreationDialog>
              <FileUploadDialog onUpload={handleFileUpload} lang={lang} isUploading={isUploadingFile}>
                {(open) => (
                  <Button
                    sx={{ flexShrink: 0 }}
                    onClick={open}
                    disabled={isUploadingFile}
                    startIcon={isUploadingFile ? <CircularProgress size={20} /> : <UploadIcon />}
                  >
                    {lang === 'en' ? 'Upload' : '上传'}
                  </Button>
                )}
              </FileUploadDialog>
            </Stack>
          </Stack>

          <Stack spacing={2}>
            <List sx={{ width: '100%' }}>
              {displayedMaterials.map((material, index) => (
                <ListItem
                  button
                  key={material._id}
                  sx={{
                    bgcolor: 'background.paper',
                    cursor: 'pointer',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    mb: 1,
                  }}
                  onClick={() => handleMaterialClick(material._id)}
                >
                  {/* Verbatim Mode Switch */}
                  {(!verbatimMode || !projectId) && <ListItemIcon>{getIcon(material.fileMetadata?.type || '')}</ListItemIcon>}
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography variant="subtitle1">{material.fileMetadata?.filename || 'Untitled'}</Typography>
                        {(material.tagIds || []).length > 0 && (
                          <>
                            {(material.tagIds || []).slice(0, 2).map((tagId: string) => {
                              const tagName = tagMap[tagId]?.name || tagId;
                              return (
                                <Chip
                                  key={tagId}
                                  label={tagName}
                                  size="small"
                                  sx={{ height: 24, maxWidth: 120, '.MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              );
                            })}
                            {(material.tagIds || []).length > 2 && (
                              <Chip
                                label={`+${(material.tagIds || []).length - 2}`}
                                size="small"
                                sx={{ height: 24 }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            )}
                          </>
                        )}
                      </Box>
                    }
                    secondary={
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography variant="body2" color="text.secondary">
                          {new Date(material.createdAt).toLocaleDateString()}
                        </Typography>
                        {projectId &&
                          (() => {
                            const hasVerbatim = Boolean(project?.verbatimMaterialId);
                            const isVerbatim = hasVerbatim && project?.verbatimMaterialId === material._id;

                            if (hasVerbatim) {
                              return (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    py: 0.25,
                                    fontSize: '0.75rem',
                                  }}
                                >
                                  {lang === 'en' ? (isVerbatim ? '• Verbatim' : '• Manual') : isVerbatim ? '• 逐字转换' : '• 手动参考'}
                                </Typography>
                              );
                            }

                            if (!hasVerbatim && material.autoReferenced) {
                              return (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    py: 0.25,
                                    fontSize: '0.75rem',
                                  }}
                                >
                                  {lang === 'en' ? '• Auto Referenced' : '• 自动参考'}
                                </Typography>
                              );
                            }

                            return null;
                          })()}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {verbatimMode && projectId && (
                      <Tooltip title={lang === 'en' ? 'Use as verbatim material' : '作为逐字转换材料'}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{
                            border: '1px solid',
                            borderColor: project?.verbatimMaterialId === material._id ? 'primary.main' : 'divider',
                            borderRadius: 2,
                            px: 1,
                            py: 0.5,
                          }}
                        >
                          <Typography variant="body2" sx={{ display: { xs: 'none', md: 'block' } }}>
                            {lang === 'en' ? 'Verbatim' : '逐字转换'}
                          </Typography>
                          <Switch
                            size="small"
                            checked={project?.verbatimMaterialId === material._id}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleVerbatimSwitchChange(material._id, e.target.checked);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            inputProps={{ 'aria-label': 'toggle verbatim mode' }}
                            edge="start"
                          />
                        </Stack>
                      </Tooltip>
                    )}
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleContextMenu(e, material);
                      }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
            {project?.verbatimMaterialId && displayedMaterials.length > 1 && (
              <Alert severity="info">
                {lang === 'en'
                  ? 'Since one material is set as verbatim, other materials will not be automatically referenced by AI.'
                  : '由于已将某个材料设置为逐字转换，其它材料将不会被 AI 自动参考。'}
              </Alert>
            )}
          </Stack>
        </>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '300px',
            gap: 2,
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {lang === 'en' ? 'No materials yet' : '暂无材料'}
          </Typography>
          <Stack direction="row" spacing={2}>
            <FileUploadDialog onUpload={handleFileUpload} lang={lang}>
              {(open) => (
                <Button variant="contained" startIcon={<UploadIcon />} onClick={open}>
                  {lang === 'en' ? 'Upload' : '上传'}
                </Button>
              )}
            </FileUploadDialog>
            <TextCreationDialog onCreate={handleCreateText} lang={lang}>
              {(open) => (
                <Button variant="outlined" startIcon={<AddIcon />} onClick={open}>
                  {lang === 'en' ? 'Create Text' : '创建文本'}
                </Button>
              )}
            </TextCreationDialog>
            {projectId && (
              <MaterialPicker
                onSelect={handleSelectMaterials}
                multiple={true}
                initialSelected={materials.map((m) => m.fileServiceFileId)}
                title={lang === 'en' ? 'Select Materials' : '选择材料'}
                lang={lang}
              >
                {(openDialog) => (
                  <Tooltip
                    title={
                      lang === 'en'
                        ? 'Link materials from existing library to current project for AI reference'
                        : '从现有材料库中链接材料到当前项目以供AI参考'
                    }
                  >
                    <span>
                      <Button variant="outlined" startIcon={<LinkIcon />} onClick={openDialog}>
                        {lang === 'en' ? 'Link Materials' : '链接材料'}
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </MaterialPicker>
            )}
          </Stack>
        </Box>
      )}

      {/* Disable Verbatim Confirmation Dialog */}
      <Dialog open={verbatimDisableDialogOpen} onClose={handleCancelDisableVerbatim} maxWidth="sm" fullWidth>
        <DialogTitle>{lang === 'en' ? 'Disable verbatim material?' : '停用逐字转换材料？'}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {lang === 'en'
              ? 'This project already has slides generated from the verbatim material. Disabling verbatim mode may make them outdated.'
              : '该项目已有根据逐字转换材料生成的幻灯片，停用逐字转换模式会导致其内容与当前材料不一致。'}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            {lang === 'en'
              ? 'Do you want to clear the existing slides so you can start fresh with AI outline generation?'
              : '是否要清空现有幻灯片以便重新开始 AI 生成流程？'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDisableVerbatim} disabled={isProcessingVerbatimDisable}>
            {lang === 'en' ? 'Cancel' : '取消'}
          </Button>
          <Button onClick={() => handleConfirmDisableVerbatim(false)} disabled={isProcessingVerbatimDisable}>
            {lang === 'en' ? 'Disable, keep slides' : '停用并保留幻灯片'}
          </Button>
          <Button
            color="error"
            variant="contained"
            startIcon={isProcessingVerbatimDisable ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            onClick={() => handleConfirmDisableVerbatim(true)}
            disabled={isProcessingVerbatimDisable}
          >
            {lang === 'en' ? 'Disable & clear slides' : '停用并清空幻灯片'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setMaterialToDelete(null);
        }}
      >
        <DialogTitle>{lang === 'en' ? 'Delete Material' : '删除材料'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, bgcolor: 'background.default', p: 1, borderRadius: 1 }}>
            {getIcon(materialToDeleteType, 20)}
            <Typography variant="subtitle1" sx={{ ml: 1 }}>
              {materialToDeleteName}
            </Typography>
          </Box>
          <Typography>
            {lang === 'en'
              ? `Are you sure you want to ${projectId ? 'remove from project' : 'delete'} this material? This action cannot be undone.`
              : `您确定要${projectId ? '从项目中删除' : '删除'}这个材料吗？此操作无法撤消。`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsDeleteDialogOpen(false);
              setMaterialToDelete(null);
            }}
          >
            {lang === 'en' ? 'Cancel' : '取消'}
          </Button>
          <Button color="error" startIcon={<DeleteIcon />} onClick={handleConfirmDelete}>
            {lang === 'en' ? 'Delete' : '删除'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        open={!!contextMenu}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
      >
        <MenuItem onClick={handleManageTags}>
          <ListItemIcon>
            <LabelIcon fontSize="small" />
          </ListItemIcon>
          {lang === 'en' ? 'Manage Tags' : '标签'}
        </MenuItem>
        <MenuItem onClick={handleToggleAutoReferenced}>
          <ListItemIcon>
            {contextMenu?.material.autoReferenced ? <CheckBoxIcon fontSize="small" color="primary" /> : <CheckBoxOutlineBlankIcon fontSize="small" />}
          </ListItemIcon>
          {lang === 'en' ? 'Auto Referenced' : '自动参考'}
        </MenuItem>
        {projectId && contextMenu?.material.projectId && (
          <MenuItem onClick={handleCopyToLibrary}>
            <ListItemIcon>
              <FileIcon fontSize="small" color="primary" />
            </ListItemIcon>
            {lang === 'en' ? 'Copy to my Materials' : '复制到我的材料库'}
          </MenuItem>
        )}
        <MenuItem onClick={handleDeleteFromContext}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          {lang === 'en' ? 'Delete' : '删除'}
        </MenuItem>
      </Menu>

      {/* Manage Tags Dialog */}
      <ManageTagsDialog
        open={manageTagsDialogOpen}
        onClose={() => setManageTagsDialogOpen(false)}
        entityType="material"
        entity={selectedMaterial}
        onSaved={handleTagsSaved}
        projectId={projectId}
      />

      {/* Feedback Snackbar */}
      <Snackbar
        open={!!feedbackMessage}
        autoHideDuration={3000}
        onClose={() => setFeedbackMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setFeedbackMessage('')} severity={feedbackType} sx={{ width: '100%' }}>
          {feedbackMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default MaterialList;
